/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, useFormikContext } from 'formik';
import { Row, Col } from 'reactstrap';
import { startCase } from 'lodash';
import { Button, Field, CardTitle } from '@cordata/buffet';
import { components } from 'react-select';

const MultiValueContainer = ({ data, ...props }) => {
  const [isDesc, setIsDesc] = useState(data.desc);

  const toggleDesc = e => {
    setIsDesc(!isDesc);

    /* eslint-disable-next-line */
    data.desc = !data.desc;

    e.stopPropagation();
  };

  const [icon, setIcon] = useState('');

  useEffect(() => {
    if (data.desc) {
      setIcon(`sort-${
        ['date', 'number'].includes(data.type) ? 'numeric' : 'alpha'
      }-up-alt`);
    } else {
      setIcon(`sort-${
        ['date', 'number'].includes(data.type) ? 'numeric' : 'alpha'
      }-down`);
    }
  }, [data.type, data.desc]);

  return (
    <div className="d-flex">
      <components.MultiValueContainer {...props} />

      <span
        onMouseDown={toggleDesc}
        className="rounded text-center font-weight-bold text-light bg-info"
        style={{
          width: 32,
          height: 30,
          fontSize: '1.4rem',
          cursor: 'pointer',
        }}
        title={`This column will be sorted in ${
          isDesc ? 'desc' : 'asc'
        }ending order.`}
      >
        <FontAwesomeIcon icon={icon} />
      </span>
    </div>
  );
};

MultiValueContainer.propTypes = {
  data: PropTypes.shape().isRequired,
};

// tanstack table's built-in aggregationFns, scoped to column types each fn can operate on
const AGGREGATION_TYPE_OPTIONS = [
  { label: 'Sum', value: 'sum', types: ['number'] },
  { label: 'Min', value: 'min', types: ['number', 'date', 'time', 'dateTime'] },
  { label: 'Max', value: 'max', types: ['number', 'date', 'time', 'dateTime'] },
  { label: 'Extent', value: 'extent', types: ['number', 'date', 'time', 'dateTime'] },
  { label: 'Mean', value: 'mean', types: ['number'] },
  { label: 'Median', value: 'median', types: ['number'] },
  {
    label: 'Unique',
    value: 'unique',
    types: ['string', 'richtext', 'boolean', 'array', 'date', 'time', 'dateTime'],
  },
  {
    label: 'Unique Count',
    value: 'uniqueCount',
    types: ['string', 'richtext', 'boolean', 'array', 'date', 'time', 'dateTime', 'number'],
  },
  { label: 'Count', value: 'count', types: null }, // null = valid for every column type
];

const aggregationTypeOptions = columnType =>
  AGGREGATION_TYPE_OPTIONS.filter(o => !o.types || !columnType || o.types.includes(columnType));

const TableForm = ({ index, availableReports, columnTypeOptions }) => {
  const {
    values,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();

  const [columnOptions, setColumnOptions] = useState([]);

  useEffect(() => {
    const reportView = values.tables[index].reportView || values.reportView;

    if (reportView) {
      setColumnOptions(reportView.columns);

      if (values.tables[index].columns) {
        // if the user imports an exported configuration, and any of the
        // columns in the report view have had their names changed, the report
        // will fail to save because of the discrepancy. show an error to the
        // user to alert them to this
        values.tables[index].columns.forEach((c, cIndex) => {
          if (
            c.columnName &&
            !reportView.columns.some(cc => cc.value === c.columnName.value)
          ) {
            const field = `tables.${index}.columns.${cIndex}.columnName`;
            const errorMessage = `A column named "${c.columnName.value}" is not present in ${reportView.value}. Please re-select the correct column.`;

            setFieldError(field, errorMessage);
            setFieldTouched(field, true);
          }
        });
      }

      if (values.tables[index].groupByColumn) {
        if (
          !reportView.columns.some(cc => cc.value === values.tables[index].groupByColumn.value)
        ) {
          const field = `tables.${index}.groupByColumn`;
          const errorMessage = `A column named "${values.tables[index].groupByColumn.value}" is not present in ${reportView.value}. Please re-select the correct column.`;

          setFieldError(field, errorMessage);
          setFieldTouched(field, true);
        }
      }

      if (values.tables[index].sortBy) {
        const field = `tables.${index}.sortBy`;
        const badColumns = [];

        values.tables[index].sortBy.forEach(s => {
          if (!reportView.columns.some(c => c.value === s.value)) {
            badColumns.push(s.value);
          }
        });

        if (badColumns.length) {
          const errorMessage = `A column named "${badColumns.join('" or "')}" is not present in ${
            reportView.value
          }. Please re-select the correct column.`;

          setFieldError(field, errorMessage);
          setFieldTouched(field, true);
        }
      }
    }
  }, [values.reportView, values.tables, index]);

  const [previousColumns, setPreviousColumns] = useState([]);

  useEffect(() => {
    const newColumns = values.tables[index].columns;

    if (newColumns) {
      newColumns.forEach((c, i) => {
        if (
          c.columnName &&
          previousColumns[i] &&
          (!previousColumns[i].columnName ||
            (previousColumns[i].columnName &&
              previousColumns[i].columnName.value !== c.columnName.value))
        ) {
          setFieldValue(
            `tables.${index}.columns.${i}.displayHeader`,
            startCase(c.columnName.label),
          );

          setFieldValue(
            `tables.${index}.columns.${i}.type`,
            columnTypeOptions.find(cto => cto.value === c.columnName.type),
          );
        }
      });
    }

    setPreviousColumns(newColumns);
  }, [values.tables, index]);

  return (
    <>
      <Row form>
        <Col sm>
          <Field name={`tables.${index}.title`} label="Table Title" />
        </Col>

        <Col sm>
          <Field
            name={`tables.${index}.reportView`}
            label="Report View"
            text="If left blank, will use base report view"
            type="select"
            options={availableReports}
          />
        </Col>
      </Row>

      <Row form>
        <Col sm>
          <Field
            name={`tables.${index}.pageSize`}
            label="Page Size"
            text="number of rows to show in table on load. user can click 'show more' to add 50 at a time. standard is 500"
            type="number"
            required
          />
        </Col>

        <Col sm>
          <Field
            name={`tables.${index}.groupByColumn`}
            label="Group Rows By Column"
            text="data in the table will be grouped and counted by the selected column. will not affect exported data"
            type="select"
            options={columnOptions}
          />
        </Col>

        <Col sm>
          <Field
            name={`tables.${index}.sortBy`}
            label="Default Sort"
            text="data in the table will be sorted by the selected columns by default"
            type="select"
            multi
            options={columnOptions}
            components={{ MultiValueContainer }}
            fixOptions={false}
          />
        </Col>
      </Row>

      {values.tables[index].groupByColumn && (
        <Row form>
          <Col sm>
            <Field
              name={`tables.${index}.groupByDisplayColumns`}
              label="Group Display Columns"
              text="when grouping, show values from these columns in the group header instead of the grouped column value. these columns will be hidden from child rows"
              type="select"
              multi
              options={columnOptions}
            />
          </Col>
        </Row>
      )}

      <fieldset>
        <CardTitle tag="h6">Columns</CardTitle>

        <FieldArray
          name={`tables.${index}.columns`}
          render={({ push, remove, move }) => (
            <>
              {values.tables[index].columns &&
                values.tables[index].columns.map((c, i) => (
                  <fieldset key={c.isPivot ? `pivot-${i}` : (c.columnName && c.columnName.value)}>
                    <Button
                      size="sm"
                      outline
                      spear
                      color="danger"
                      type="button"
                      onClick={() => remove(i)}
                    >
                      <FontAwesomeIcon icon="minus" className="mr-2" />
                      Remove Column
                    </Button>

                    <Row form>
                      {values.tables[index].columns.length > 1 && (
                        <Col md={1}>
                          <Row form style={{ height: '50%' }}>
                            <Button
                              className="m-auto"
                              outline
                              type="button"
                              onClick={() => move(i, i - 1)}
                              disabled={i === 0}
                            >
                              <FontAwesomeIcon icon="caret-up" />
                            </Button>
                          </Row>

                          <Row form style={{ height: '50%' }}>
                            <Button
                              className="m-auto"
                              outline
                              type="button"
                              onClick={() => move(i, i + 1)}
                              disabled={
                                i === values.tables[index].columns.length - 1
                              }
                            >
                              <FontAwesomeIcon icon="caret-down" />
                            </Button>
                          </Row>
                        </Col>
                      )}

                      {c.isPivot ? (
                        <>
                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.keyColumn`}
                              label="Pivot Key Column"
                              text="Values from this column become dynamic column headers"
                              type="select"
                              options={columnOptions}
                              required
                            />
                          </Col>

                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.valueColumn`}
                              label="Pivot Value Column"
                              text="Values from this column fill the dynamic cells"
                              type="select"
                              options={columnOptions}
                              required
                            />
                          </Col>

                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.type`}
                              label="Value Type"
                              text="How dynamic column values will be displayed"
                              type="select"
                              options={columnTypeOptions}
                              required
                            />
                          </Col>
                        </>
                      ) : (
                        <>
                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.columnName`}
                              label="View Column Name"
                              text="The column in the report view"
                              type="select"
                              options={columnOptions}
                              required
                            />
                          </Col>

                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.displayHeader`}
                              label="Display Header"
                              text="The text that will be shown in the UI as the column's header"
                              required
                            />
                          </Col>

                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.type`}
                              label="Type"
                              text="Column type. Affects how data will be displayed"
                              type="select"
                              options={columnTypeOptions}
                              required
                            />
                          </Col>

                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.aggregationType`}
                              label="Aggregation Type"
                              text="Aggregation type. Affects how data will be aggregated for the grouped by column"
                              type="select"
                              options={aggregationTypeOptions(c.type && c.type.value)}
                            />
                          </Col>
                        </>
                      )}
                    </Row>

                    {!c.isPivot && (
                      <Row form>
                        <Col sm="auto">
                          <Field
                            name={`tables.${index}.columns.${i}.showCustomNullText`}
                            label="Display custom text for null values?"
                            type="checkbox"
                          />
                        </Col>

                        {values.tables[index].columns[i].showCustomNullText && (
                          <Col sm>
                            <Field
                              name={`tables.${index}.columns.${i}.nullText`}
                              label="Null value text"
                              text="This text will be displayed when the column value is null"
                            />
                          </Col>
                        )}
                      </Row>
                    )}

                    {i !== values.tables[index].columns.length - 1 && <hr />}
                  </fieldset>
                ))}

              <Button
                size="sm"
                outline
                color="tertiary"
                spear
                type="button"
                onClick={() => push({})}
              >
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Add Column
              </Button>

              <Button
                size="sm"
                outline
                color="info"
                spear
                type="button"
                className="ml-2"
                onClick={() => push({ isPivot: true, type: { label: 'Dynamic', value: 'dynamic' } })}
              >
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Add Pivot Column
              </Button>
            </>
          )}
        />
      </fieldset>
    </>
  );
};

export default TableForm;
